import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiPlus,
  FiSearch,
  FiShield,
  FiAlertCircle,
  FiArrowRight,
  FiUsers,
  FiLoader
} from 'react-icons/fi';

import SafeIcon from '../../common/SafeIcon';
import { useClient } from '../../context/ClientContext';
import { useNavigate } from 'react-router-dom';
import CreateBusinessModal from './CreateBusinessModal';

const formatLabel = (value) =>
  value
    ? String(value)
        .split('_')
        .map((word) =>
          word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join(' ')
    : '';

const getFirmTypeLabel = (type) => {
  if (type === 'accounting_firm') return 'Accounting Firm';
  if (type === 'accountant_practice') return 'Accounting Practice';
  return formatLabel(type) || 'Firm Workspace';
};

export default function ClientSwitcher() {
  const {
    activeFirm,
    activeClient,
    clients,
    switchClient,
    workspaceError,
    isLoading,
    refreshProfile
  } = useClient();

  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);

  const hasFirm = Boolean(activeFirm?.id);
  const firmRole = activeFirm?.role || null;
  const canCreateClient =
    hasFirm && firmRole === 'owner' && !workspaceError;

  const clientList = Array.isArray(clients) ? clients : [];

  const filteredClients = clientList.filter((client) =>
    String(client?.name || '')
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  const closeMenu = () => {
    setIsOpen(false);
    setSearch('');
  };

  useEffect(() => {
    const onPointerDown = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const handleClientSelect = async (clientId) => {
    if (
      switchingId ||
      !clientList.some((client) => client.id === clientId)
    ) {
      return;
    }

    if (activeClient?.id === clientId) {
      closeMenu();
      return;
    }

    setSwitchingId(clientId);
    closeMenu();

    try {
      await switchClient(clientId);
    } catch (error) {
      console.error('Client switch failed:', error);
    } finally {
      setSwitchingId(null);
    }
  };

  const openCreateModal = () => {
    if (!canCreateClient) return;

    closeMenu();
    setIsCreating(true);
  };

  const goToClients = () => {
    closeMenu();
    navigate('/clients');
  };

  const handleModalClose = async () => {
    setIsCreating(false);

    // Refresh the authorized workspace lists after
    // the modal closes. No client is created here.
    if (typeof refreshProfile === 'function') {
      await refreshProfile();
    }
  };

  const firmInitial =
    activeFirm?.name?.trim()?.charAt(0)?.toUpperCase() || null;

  return (
    <>
      <div ref={dropdownRef} className="relative w-full">
        {/* HEADER SWITCHER */}
        <button
          type="button"
          onClick={() => setIsOpen((previous) => !previous)}
          aria-label="Open workspace and client selector"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          className={`group flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors duration-150 ${
            isOpen
              ? 'border-emerald-300 bg-emerald-50/60'
              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
          }`}
        >
          {/* FIRM AVATAR */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-sm font-bold text-white">
            {firmInitial || (
              <SafeIcon icon={FiBriefcase} className="text-base" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-bold leading-tight text-slate-900">
              {isLoading
                ? 'Loading workspace...'
                : activeFirm?.name || 'Workspace unavailable'}
            </p>

            <p className="mt-1 truncate text-[11px] text-slate-500">
              {activeClient
                ? `Working on ${activeClient.name}`
                : hasFirm
                  ? `${formatLabel(firmRole)} · ${getFirmTypeLabel(activeFirm.type)}`
                  : 'No verified firm selected'}
            </p>
          </div>

          <SafeIcon
            icon={FiChevronDown}
            className={`shrink-0 text-sm text-slate-400 transition-transform duration-150 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* DROPDOWN */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              role="dialog"
              aria-label="Workspace selector"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 top-full z-50 mt-2 w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10"
            >
              {/* FIRM SECTION */}
              <div className="border-b border-slate-100 p-4">
                <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                  Your Workspace
                </p>

                {hasFirm ? (
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-base font-bold text-white">
                      {firmInitial}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="break-words text-[14px] font-bold leading-snug text-slate-900">
                        {activeFirm.name}
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        {getFirmTypeLabel(activeFirm.type)}
                      </p>

                      <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                        <SafeIcon icon={FiShield} className="text-xs" />
                        {formatLabel(firmRole)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                    <SafeIcon
                      icon={FiAlertCircle}
                      className="mt-0.5 shrink-0"
                    />
                    <p>
                      {workspaceError ||
                        'No managing-firm membership was found for this account.'}
                    </p>
                  </div>
                )}
              </div>

              {/* CLIENT SECTION */}
              <div className="p-3">
                <div className="mb-3 flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <SafeIcon
                      icon={FiUsers}
                      className="text-sm text-slate-400"
                    />
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      Clients
                    </span>
                  </div>

                  <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                    {clientList.length}
                  </span>
                </div>

                {workspaceError && (
                  <div
                    role="alert"
                    className="mb-3 flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-3 text-xs text-rose-700"
                  >
                    <SafeIcon
                      icon={FiAlertCircle}
                      className="mt-0.5 shrink-0"
                    />
                    <span>{workspaceError}</span>
                  </div>
                )}

                {isLoading ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-xs text-slate-500">
                    <SafeIcon
                      icon={FiLoader}
                      className="animate-spin"
                    />
                    Loading clients...
                  </div>
                ) : clientList.length > 0 ? (
                  <>
                    {/* SEARCH */}
                    <div className="relative mb-3">
                      <SafeIcon
                        icon={FiSearch}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400"
                      />

                      <input
                        type="search"
                        autoComplete="off"
                        aria-label="Search clients"
                        placeholder="Search clients..."
                        value={search}
                        onChange={(event) =>
                          setSearch(event.target.value)
                        }
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-xs text-slate-800 outline-none transition-colors focus:border-emerald-500"
                      />
                    </div>

                    {/* CLIENT LIST */}
                    <div className="max-h-64 space-y-1 overflow-y-auto">
                      {filteredClients.map((client) => {
                        const selected =
                          client.id === activeClient?.id;

                        return (
                          <button
                            type="button"
                            key={client.id}
                            disabled={Boolean(switchingId)}
                            onClick={() =>
                              handleClientSelect(client.id)
                            }
                            aria-current={
                              selected ? 'true' : undefined
                            }
                            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors disabled:opacity-60 ${
                              selected
                                ? 'bg-emerald-50'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <div
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                selected
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              <SafeIcon
                                icon={FiBriefcase}
                                className="text-sm"
                              />
                            </div>

                            <div className="min-w-0 flex-1">
                              <p className={`truncate text-[13px] font-semibold ${
                                selected
                                  ? 'text-emerald-800'
                                  : 'text-slate-800'
                              }`}>
                                {client.name}
                              </p>

                              <p className="mt-0.5 text-[11px] text-slate-400">
                                {formatLabel(client.status || 'active')}
                              </p>
                            </div>

                            {selected && (
                              <SafeIcon
                                icon={FiCheck}
                                className="text-emerald-600"
                              />
                            )}
                          </button>
                        );
                      })}

                      {filteredClients.length === 0 && (
                        <p className="py-6 text-center text-xs text-slate-500">
                          No clients match your search.
                        </p>
                      )}
                    </div>
                  </>
                ) : !workspaceError && hasFirm ? (
                  /* FIRST CLIENT EMPTY STATE */
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-5 py-6 text-center">
                    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500">
                      <SafeIcon
                        icon={FiBriefcase}
                        className="text-lg"
                      />
                    </div>

                    <p className="text-sm font-bold text-slate-900">
                      No clients yet
                    </p>

                    <p className="mx-auto mt-1 max-w-[240px] text-xs leading-relaxed text-slate-500">
                      Your firm is ready. Add a client to begin managing their finances.
                    </p>

                    {canCreateClient && (
                      <button
                        type="button"
                        onClick={openCreateModal}
                        className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                      >
                        <SafeIcon icon={FiPlus} />
                        Add First Client
                      </button>
                    )}
                  </div>
                ) : null}

                {/* FOOTER ACTIONS */}
                {hasFirm && !workspaceError && clientList.length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    {canCreateClient && (
                      <button
                        type="button"
                        onClick={openCreateModal}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                      >
                        <SafeIcon icon={FiPlus} />
                        Add Client
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        closeMenu();
                        navigate('/clients');
                      }}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      View All Clients
                      <SafeIcon
                        icon={FiArrowRight}
                        className="text-sm"
                      />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* EXISTING CLIENT CREATION WORKFLOW */}
      <CreateBusinessModal
        isOpen={isCreating && canCreateClient}
        onClose={handleModalClose}
      />
    </>
  );
}